import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PresentationStage } from './PresentationStage';
import { VideoTile } from './VideoTile';
import type { ParticipantView } from '../lib/livekit';
import {
  emptySlots,
  placeTiles,
  splitByOrder,
  tileLimit,
  type SlotState,
} from '../lib/visibleTiles';
import './VideoGrid.css';

/**
 * Раскладка встречи.
 *
 * Четыре состояния, в порядке приоритета:
 *
 *   0. Включён режим презентации — раскладку задаёт организатор, и она
 *      одна на всех: показ во весь кадр, докладчик поверх. Ручной выбор
 *      здесь не действует намеренно, иначе «одинаково у всех» перестало
 *      бы выполняться.
 *   1. Участник выбран вручную (клик или тап по плитке) — он на сцене.
 *   2. Кто-то демонстрирует экран — экран на сцене.
 *   3. Обычная сетка: число колонок подбирается так, чтобы плитки были
 *      максимально близки к 16:9.
 */
export function VideoGrid({
  participants,
  presentation = false,
}: {
  participants: ParticipantView[];
  presentation?: boolean;
}) {
  const [pinnedSid, setPinnedSid] = useState<string | null>(null);

  const pinned = participants.find((p) => p.sid === pinnedSid) ?? null;
  const sharing = participants.find((p) => p.screenShareEnabled);

  // Выбранный участник вышел из встречи — возвращаемся к сетке, иначе
  // сцена осталась бы пустой навсегда.
  useEffect(() => {
    if (pinnedSid && !pinned) setPinnedSid(null);
  }, [pinnedSid, pinned]);

  /*
   * Началась демонстрация экрана — снимаем ручной выбор.
   *
   * Иначе человек, закрепивший собеседника, просто не увидел бы, что ему
   * показывают: сцена осталась бы занята. Уже идущая демонстрация выбор
   * не перебивает — сравниваем именно с предыдущим состоянием, поэтому
   * закрепить кого-то поверх демонстрации по-прежнему можно.
   */
  const sharingSid = sharing?.sid ?? null;
  const previousSharing = useRef(sharingSid);
  useEffect(() => {
    if (sharingSid && sharingSid !== previousSharing.current) setPinnedSid(null);
    previousSharing.current = sharingSid;
  }, [sharingSid]);

  const toggle = useCallback(
    (sid: string) => setPinnedSid((current) => (current === sid ? null : sid)),
    [],
  );

  if (presentation) return <PresentationStage participants={participants} />;

  if (pinned) {
    return (
      <div className="stage">
        <div className="stage__main">
          <VideoTile participant={pinned} featured pinned onActivate={() => toggle(pinned.sid)} />
        </div>
        {/* Того, кто уже на сцене, в ленте не повторяем. */}
        <Rail
          participants={participants.filter((p) => p.sid !== pinned.sid)}
          onPick={toggle}
          lead={
            /*
              Демонстрация экрана идёт, но сцену занял выбранный человек —
              показываем экран в ленте отдельной плиткой. Иначе о том, что
              вам вообще что-то показывают, узнать было неоткуда: сцена
              занята, а в ленте только лица. Плитка идёт первой и не
              участвует в отборе: вытеснить показ лицами нельзя.
            */
            sharing ? (
              <div className="stage__rail-item" key={`${sharing.sid}-screen`}>
                <VideoTile
                  participant={sharing}
                  source="screen"
                  onActivate={() => setPinnedSid(null)}
                />
              </div>
            ) : null
          }
        />
      </div>
    );
  }

  if (sharing) {
    return (
      <div className="stage">
        <div className="stage__main">
          <VideoTile participant={sharing} source="screen" featured />
        </div>
        {/* Здесь на сцене экран, а в ленте лица — это не повтор. */}
        <Rail participants={participants} onPick={toggle} />
      </div>
    );
  }

  return <AutoGrid participants={participants} onPick={toggle} />;
}

function AutoGrid({
  participants,
  onPick,
}: {
  participants: ParticipantView[];
  onPick: (sid: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(1);
  const limit = useTileLimit();
  const { visible, hiddenCount } = useVisibleTiles(participants, limit);

  // Плитка «и ещё N» занимает в сетке такое же место, как участник, —
  // значит и в расчёт колонок входит наравне с ними.
  const cells = visible.length + (hiddenCount > 0 ? 1 : 0);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const layout = () => {
      const { width, height } = host.getBoundingClientRect();
      setColumns(bestColumnCount(cells, width, height));
    };

    layout();
    const observer = new ResizeObserver(layout);
    observer.observe(host);
    return () => observer.disconnect();
  }, [cells]);

  return (
    <div ref={hostRef} className="grid" style={{ '--cols': columns } as React.CSSProperties}>
      {visible.map((p) => (
        <VideoTile key={p.sid} participant={p} onActivate={() => onPick(p.sid)} />
      ))}

      {/*
        Скрытые участники — не ошибка и не пропажа: они во встрече, их
        слышно, они в списке участников. Но человек должен знать, что
        видит не всех, иначе это читается как сбой.
      */}
      {hiddenCount > 0 && (
        <div className="grid__more" title="Показаны говорящие и последние активные">
          <span className="grid__more-count">+{hiddenCount}</span>
          <span className="grid__more-text">
            {hiddenCount === 1 ? 'участник без видео' : 'участников не поместились'}
          </span>
        </div>
      )}
    </div>
  );
}

/** Лента лиц под сценой — с тем же ограничением, что и сетка. */
function Rail({
  participants,
  onPick,
  lead = null,
}: {
  participants: ParticipantView[];
  onPick: (sid: string) => void;
  /** Плитка перед остальными — идёт вне отбора. */
  lead?: React.ReactNode;
}) {
  // В ленте плитки мельче, поэтому их влезает вдвое больше, чем в сетке.
  const limit = useTileLimit() * 2;
  const { visible, hiddenCount } = useVisibleTiles(participants, limit);

  return (
    <div className="stage__rail">
      {lead}
      {visible.map((p) => (
        <div className="stage__rail-item" key={p.sid}>
          <VideoTile participant={p} onActivate={() => onPick(p.sid)} />
        </div>
      ))}
      {hiddenCount > 0 && (
        <div className="stage__rail-item stage__rail-more" title="Показаны говорящие и последние активные">
          +{hiddenCount}
        </div>
      )}
    </div>
  );
}

/**
 * Предел числа плиток, пересчитываемый при изменении окна.
 *
 * Поворот телефона и разделение экрана на ноутбуке меняют доступную
 * ширину вдвое — предел должен следовать за ней, иначе после поворота
 * на узком экране остаётся дюжина плиток размером с ноготь.
 */
function useTileLimit(): number {
  const [limit, setLimit] = useState(() =>
    tileLimit(typeof window === 'undefined' ? 1280 : window.innerWidth),
  );

  useEffect(() => {
    const update = () => setLimit(tileLimit(window.innerWidth));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return limit;
}

/**
 * Держит состав видимых плиток.
 *
 * Пересчитывается на изменение списка участников — то есть на события
 * комнаты, а не на каждый кадр.
 *
 * Пересчёт живёт в эффекте, а не в `useMemo`, хотя так на событие
 * комнаты приходится лишняя перерисовка. Причина в том, что расчёт
 * накапливает состояние — кто когда говорил, — а менять состояние во
 * время отрисовки нельзя: в StrictMode компонент рисуется дважды, и
 * такой расчёт молча выполнялся бы два раза с разными отметками
 * времени. Цена — одна дополнительная перерисовка на приход или уход
 * участника, а это событие редкое.
 */
function useVisibleTiles(participants: ParticipantView[], limit: number) {
  // Первое размещение считаем сразу, иначе первый кадр встречи был бы
  // пустым: своя плитка появилась бы только после эффекта.
  const [slots, setSlots] = useState<SlotState>(() =>
    placeTiles(participants, emptySlots(), limit),
  );

  useEffect(() => {
    setSlots((previous) => placeTiles(participants, previous, limit));
  }, [participants, limit]);

  return useMemo(
    () => splitByOrder(participants, slots.order, limit),
    [participants, slots, limit],
  );
}

const GAP = 12;
const TARGET_RATIO = 16 / 9;

/** Перебирает варианты сетки и выбирает тот, где плитки крупнее всего. */
function bestColumnCount(count: number, width: number, height: number): number {
  if (count <= 1 || width <= 0 || height <= 0) return 1;

  let best = 1;
  let bestArea = 0;

  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);

    const cellWidth = (width - GAP * (cols - 1)) / cols;
    const cellHeight = (height - GAP * (rows - 1)) / rows;
    if (cellWidth <= 0 || cellHeight <= 0) continue;

    // Плитка сохраняет 16:9, вписываясь в ячейку.
    const tileWidth = Math.min(cellWidth, cellHeight * TARGET_RATIO);
    const area = tileWidth * (tileWidth / TARGET_RATIO);

    if (area > bestArea) {
      bestArea = area;
      best = cols;
    }
  }

  return best;
}
